import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { sessionKeys } from "../../shared/data/queryKeys";
import { queryClient } from "../../shared/data/queryClient";
import { normalizeKeys } from "../../shared/utils/normalize";
import {
  getPlanEntitlements,
  normalizeEntitlements,
} from "../../shared/utils/planEntitlements";
import { apiRpc } from "../../shared/data/supabaseApi";
import { useSessionContextQuery } from "../auth/sessionContext";

function failClosedEntitlements() {
  return getPlanEntitlements(null);
}

export function resolveEntitlementsFromSessionContext(sessionContext) {
  const normalized = sessionContext ? normalizeKeys(sessionContext) : null;
  const organization = normalized?.organization
    ? normalizeKeys(normalized.organization)
    : null;
  const fallback = getPlanEntitlements(organization?.planTier || null, {
    seatsAllowed:
      typeof organization?.seatsAllowed === "number"
        ? organization.seatsAllowed
        : null,
  });
  return normalizeEntitlements(
    normalized?.entitlements || organization?.entitlements || null,
    organization?.planTier || null,
    {
      seatsAllowed:
        typeof organization?.seatsAllowed === "number"
          ? organization.seatsAllowed
          : null,
    },
  ) || fallback;
}

export function getCachedSessionEntitlements() {
  const cached = queryClient.getQueryData(sessionKeys.context());
  const base = resolveEntitlementsFromSessionContext(cached);
  const teamId = cached?.team?.id || cached?.teamId || cached?.team_id || null;
  const billing = teamId
    ? queryClient.getQueryData(["billing-entitlements", teamId])
    : null;
  return resolveEntitlementsWithBillingState(base, billing, {
    billingReady: Boolean(billing),
  });
}

export function resolveEntitlementsWithBillingState(
  baseEntitlements,
  billingState,
  { billingReady = true } = {},
) {
  const base = baseEntitlements || failClosedEntitlements();
  if (!billingReady) return failClosedEntitlements();
  if (!billingState || typeof billingState !== "object") return base;

  const normalized = normalizeKeys(billingState);
  const state = String(normalized.entitlementState || "")
    .trim()
    .toLowerCase();
  if (!state || (state === "pending" && Number(normalized.version || 0) === 0)) {
    return base;
  }
  if (state === "active") {
    return normalizeEntitlements(
      normalized.planEntitlements
        ? normalizeKeys(normalized.planEntitlements)
        : null,
      base.planTier,
      { seatsAllowed: normalized.seatLimit },
    );
  }
  if (state === "past_due") {
    const graceEndsAt = Date.parse(String(normalized.graceEndsAt || ""));
    if (Number.isFinite(graceEndsAt) && graceEndsAt > Date.now()) return base;
  }
  return failClosedEntitlements();
}

export function useEntitlements(options) {
  const query = useSessionContextQuery(options);
  const teamId =
    query.data?.team?.id || query.data?.teamId || query.data?.team_id || null;
  const billingQuery = useQuery({
    queryKey: ["billing-entitlements", teamId],
    queryFn: async () => {
      const { data, error } = await apiRpc("get_billing_entitlements", {
        p_team_id: teamId,
      });
      if (error) throw error;
      return data || null;
    },
    enabled:
      options?.enabled !== false &&
      Boolean(teamId) &&
      query.isLoading !== true &&
      !query.error,
    staleTime: 60 * 1000,
    retry: false,
  });
  const entitlements = useMemo(
    () => resolveEntitlementsWithBillingState(
      resolveEntitlementsFromSessionContext(query.data),
      billingQuery.data,
      {
        billingReady: !teamId || billingQuery.isSuccess,
      },
    ),
    [billingQuery.data, billingQuery.isSuccess, query.data, teamId],
  );
  const isOrgOwner = query.data?.orgRole === "owner";

  return {
    ...query,
    billingQuery,
    entitlements,
    isOrgOwner,
  };
}
